import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useAccountUpgrade } from "@/hooks/use-account-upgrade";
import { isUpgradeLink } from "@/lib/firebase";
import { getAuthHeaders } from "@/lib/queryClient";
import { CheckCircle, AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { User } from "firebase/auth";
import type { AuthLinkMode } from "@/lib/firebase";
import { apiUrl } from "@/lib/config";

interface CompleteUpgradeProps {
  mode?: AuthLinkMode;
}

export default function CompleteUpgrade({ mode = "upgrade" }: CompleteUpgradeProps) {
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const { step, error, completeLink } = useAccountUpgrade();
  const [attempted, setAttempted] = useState(false);

  const isLogin = mode === "login";

  useEffect(() => {
    if (attempted) return;

    const url = window.location.href;

    // Verify this is actually a Firebase email link
    if (!isUpgradeLink(url)) {
      navigate("/app/chat", { replace: true });
      return;
    }

    // For upgrade mode, we need the anonymous user to be in place.
    // For login mode we can proceed without a current user — signInWithEmailLink creates one.
    if (!isLogin && !user) return;

    // Pre-hydrate the onboarding-gate keys synchronously, BEFORE we trigger
    // the Firebase auth state change. Otherwise on a fresh browser App.tsx's
    // useEffect would see empty localStorage when `user` flips, set
    // showOnboarding=true, and bounce the user into WelcomeFlow after they
    // click "Continue to Chat". The async syncToBackend below overwrites
    // these placeholders with the real server profile values.
    if (isLogin) {
      localStorage.setItem("vyzle-chat-onboarded", "true");
      if (!localStorage.getItem("vyzle-chat-language")) {
        const browserLang = navigator.language?.split("-")[0] || "en";
        localStorage.setItem("vyzle-chat-language", browserLang);
      }
    }

    setAttempted(true);

    const syncToBackend = async (authedUser: User) => {
      try {
        await fetch(apiUrl(`/api/users/${authedUser.uid}`), {
          method: "PATCH",
          headers: { "Content-Type": "application/json", ...(await getAuthHeaders()) },
          body: JSON.stringify({
            email: authedUser.email,
            displayName: authedUser.displayName || undefined,
            authProvider: "email-link",
            isAnonymous: false,
          }),
        });
      } catch (err) {
        console.warn("Failed to sync authenticated user to backend:", err);
      }

      // On login mode (new device / browser), hydrate localStorage from the
      // server profile so App.tsx's onboarding gate doesn't bounce the user
      // back into WelcomeFlow. Upgrade mode doesn't need this — the user
      // already completed onboarding on this device.
      if (isLogin) {
        try {
          const res = await fetch(apiUrl(`/api/users/${authedUser.uid}`));
          if (res.ok) {
            const profile = await res.json();
            if (profile?.preferredLanguage) {
              localStorage.setItem("vyzle-chat-language", profile.preferredLanguage);
            }
            if (profile?.displayName) {
              localStorage.setItem("vyzle-chat-user-name", profile.displayName);
            }
            if (profile?.pronouns) {
              localStorage.setItem("vyzle-chat-pronouns", profile.pronouns);
            }
            localStorage.setItem("vyzle-chat-onboarded", "true");
          }
        } catch (err) {
          // Best-effort: if this fails the onboarding gate will just re-prompt,
          // which is harmless (the user is signed in).
          console.warn("Failed to hydrate local profile after login:", err);
        }
      }
    };

    completeLink(url, syncToBackend, mode);
  }, [user, attempted, completeLink, navigate, mode, isLogin]);

  const goToChat = () => {
    navigate("/app/chat", { replace: true });
  };

  return (
    <div className="min-h-dvh flex items-center justify-center bg-[var(--chat-bg)] px-4">
      <div className="max-w-sm w-full text-center space-y-6">
        {/* Loading */}
        {(step === "idle" || step === "completing") && (
          <>
            <Loader2 className="w-12 h-12 mx-auto animate-spin text-[var(--accent)]" />
            <div>
              <h2 className="text-xl font-medium dark:text-white text-gray-900">
                {isLogin ? "Signing you in..." : "Upgrading your account..."}
              </h2>
              <p className="text-sm text-muted-foreground mt-2">
                {isLogin
                  ? "Verifying your sign-in link. This only takes a moment."
                  : "Linking your email. This only takes a moment."}
              </p>
            </div>
          </>
        )}

        {/* Success */}
        {step === "done" && (
          <>
            <div className="w-16 h-16 mx-auto bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-medium dark:text-white text-gray-900">
                {isLogin ? "Welcome back!" : "Account Upgraded!"}
              </h2>
              <p className="text-sm text-muted-foreground mt-2">
                {isLogin
                  ? "You're signed in. Your chats, rooms, and settings are ready."
                  : "Your account is now secured with your email. You can access your chats from any device."}
              </p>
            </div>
            <Button
              onClick={goToChat}
              className="w-full bg-gradient-to-r from-[var(--bubble-own-from)] to-[var(--bubble-own-to)] text-white"
            >
              Continue to Chat
            </Button>
          </>
        )}

        {/* Error */}
        {step === "error" && (
          <>
            <div className="w-16 h-16 mx-auto bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-medium dark:text-white text-gray-900">
                {isLogin ? "Sign-In Failed" : "Upgrade Failed"}
              </h2>
              <p className="text-sm text-muted-foreground mt-2">{error}</p>
            </div>
            <Button onClick={goToChat} variant="outline" className="w-full">
              Back to Chat
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
